import { useQuery } from '@apollo/client'
import { useState, useEffect } from 'react'
import { ListOptions, Loader } from '../../components'
import { GET_CAMPAIGNS_BY_BRAND_ID } from '../../gql'
import {
  Brand,
  CampaignStatus,
  CountryCode,
  GetCampaignsSort,
  User,
} from '../../gql/types'
import { useCampaignFilters } from './hooks/useCampaignFilters'
import { Grid } from './components/Grid'
import { Header } from './components/Header'
import { List } from './components/List'

interface IProps {
  user: User
  brand: Brand
}

const sortOptions = [
  { name: 'Newest', value: GetCampaignsSort.newest },
  { name: 'Oldest', value: GetCampaignsSort.oldest },
  { name: 'Last Updated', value: GetCampaignsSort.lastUpdated },
]

const filterOptions = [
  {
    id: 'status',
    name: 'Status',
    options: [
      { value: CampaignStatus.draft, label: 'Draft' },
      { value: CampaignStatus.active, label: 'Active' },
      { value: CampaignStatus.paused, label: 'Paused' },
      { value: CampaignStatus.completed, label: 'Completed' },
      // { value: CampaignStatus.refunded, label: 'Refunded' },
    ],
  },
  {
    id: 'countryCode',
    name: 'Country',
    options: [
      { value: CountryCode.US, label: '\u{1F1FA}\u{1F1F8} United States' },
      { value: CountryCode.GB, label: '\u{1F1EC}\u{1F1E7} United Kingdom' },
      { value: CountryCode.AU, label: '\u{1F1E6}\u{1F1FA} Australia' },
    ],
  },
]

const Campaigns = ({ brand }: IProps) => {
  const limit = 20
  const [skip, setSkip] = useState(0)

  const [query, setQuery] = useState('')

  // Use modern hooks for persistent filters (status + country filters, not search) per brand
  const {
    filters: persistentFilters,
    updateStatus,
    updateCountryCode,
  } = useCampaignFilters(brand.id)

  // Local state for immediate UI updates, initialized from persistent filters
  const [filtersValue, setFiltersValue] = useState(() => ({
    status: persistentFilters.status,
    countryCode: persistentFilters.countryCode,
  }))

  // Sync filtersValue when persistentFilters change (e.g., when switching brands)
  useEffect(() => {
    setFiltersValue({
      status: persistentFilters.status,
      countryCode: persistentFilters.countryCode,
    })
  }, [persistentFilters.status, persistentFilters.countryCode])

  const { loading, error, data, refetch } = useQuery(
    GET_CAMPAIGNS_BY_BRAND_ID,
    {
      fetchPolicy: 'cache-and-network',
      variables: {
        brandId: brand.id,
        options: {
          query: '', // Search stays empty initially
          filters: {
            status: Object.keys(persistentFilters.status).filter(
              (key) => persistentFilters.status[key] === true,
            ),
            countryCode: Object.keys(persistentFilters.countryCode).filter(
              (key) => persistentFilters.countryCode[key] === true,
            ),
          },
          sort: GetCampaignsSort.lastUpdated,
          skip,
          limit,
        },
      },
    },
  )

  const refetchData = (query, filters, sort = null) => {
    refetch({
      options: {
        query: query,
        filters: {
          status: Object.keys(filters.status).filter(
            (key) => filters.status[key] === true,
          ),
          countryCode: Object.keys(filters.countryCode).filter(
            (key) => filters.countryCode[key] === true,
          ),
        },
        sort: sort,
        skip,
        limit,
      },
    })
  }

  const onSearch = () => {
    refetchData(query, filtersValue)
  }

  const onFiltersUpdate = (section, option) => {
    // Update local state for immediate UI feedback
    const newFiltersValue = {
      ...filtersValue,
      [section.id]: {
        ...filtersValue[section.id],
        [option.value]: !filtersValue[section.id][option.value],
      },
    }
    setFiltersValue(newFiltersValue)

    // Clear search when filters change (search no longer relevant)
    setQuery('')

    // Update persistent state (useQuery will react to persistentFilters change)
    if (section.id === 'status') {
      updateStatus(option.value, !filtersValue.status[option.value])
    } else if (section.id === 'countryCode') {
      updateCountryCode(option.value, !filtersValue.countryCode[option.value])
    }
  }

  const onSort = (sort) => {
    refetchData(query, filtersValue, sort)
  }

  if (error) {
    console.log(error)
    return <p>There has been an error. Try refreshing the page</p>
  }

  const setPage = (page: number) => {
    setSkip(page * limit)
  }

  return (
    <div>
      <Header brand={brand} />

      {loading && <Loader />}
      {!loading && data && (
        <div className='pb-4 w-auto h-fit'>
          <div className='px-1'>
            <ListOptions
              query={query}
              setQuery={setQuery}
              onSearch={onSearch}
              filterOptions={filterOptions}
              filtersValue={filtersValue}
              onFiltersUpdate={onFiltersUpdate}
              sortOptions={sortOptions}
              onSort={onSort}
            />
          </div>
          <div className='overflow-x-auto'>
            <div className='inline-block min-w-full py-2 px-1 align-middle'>
              <div className='shadow ring-1 ring-black ring-opacity-5 rounded-t'>
                <div className='hidden lg:block'>
                  <List
                    campaigns={data?.getCampaignsByBrandId?.items}
                    pagination={data?.getCampaignsByBrandId?.pagination}
                    setPage={setPage}
                  />
                </div>
                <div className='block lg:hidden'>
                  <Grid
                    campaigns={data?.getCampaignsByBrandId?.items}
                    pagination={data?.getCampaignsByBrandId?.pagination}
                    setPage={setPage}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export { Campaigns }
